'use client';

import { useCallback, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * The neutral platform default brand name (BRAND-3, #1764). Rendered by
 * <TenantBrand> whenever no tenant brand is supplied: on pre-auth surfaces
 * (login, redeem) there is no resolved tenant yet (BRAND-4, #1765 wires that),
 * so the mark MUST fall back to a neutral platform wordmark and NEVER to the
 * retired "green team" identity.
 */
export const PLATFORM_BRAND_NAME = 'Recreated';

/**
 * The neutral platform default tagline, shown on pre-auth surfaces (login) when
 * no tenant tagline is resolved. Kept identical to the previous hardcoded login
 * copy so the platform default renders with no visible regression.
 */
export const PLATFORM_BRAND_TAGLINE = 'The most elite sales training on the planet';

type BrandTheme = 'light' | 'dark';
type BrandSize = 'sm' | 'md' | 'lg';

const wordmarkSizes: Record<BrandSize, string> = {
  sm: 'text-lg',
  md: 'text-2xl',
  lg: 'text-3xl',
};

const logoSizes: Record<BrandSize, string> = {
  sm: 'h-7',
  md: 'h-9',
  lg: 'h-12',
};

const taglineSizes: Record<BrandSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
};

export type TenantBrandProps = {
  /** Tenant brand name. Falls back to the platform default when absent. */
  brandName?: string | null;
  /** Tenant tagline. Rendered only when `showTagline` is set. */
  tagline?: string | null;
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  /** Which logo variant to show for the current theme. Defaults to dark. */
  theme?: BrandTheme;
  size?: BrandSize;
  /** Render the tagline below the mark when a tagline is provided. */
  showTagline?: boolean;
  /**
   * Hold the text wordmark to a single clipped line (#1935). Off by default: a
   * roomy surface (the login card) would rather wrap a long tenant name onto a
   * second line than lose the end of it. Fixed-height chrome that sits beside
   * other controls turns that wrap into an overflow, so the mobile top bar
   * opts in. Only affects the wordmark; a logo is already bounded by its
   * fixed height and max-w-full.
   */
  truncateWordmark?: boolean;
  className?: string;
};

// Pick the logo variant for the active theme, falling back to the other
// variant so a tenant that set only one logo still shows it.
function logoForTheme(
  theme: BrandTheme,
  logoLightUrl: string | null | undefined,
  logoDarkUrl: string | null | undefined,
): string | null {
  const preferred = theme === 'light' ? logoLightUrl : logoDarkUrl;
  const fallback = theme === 'light' ? logoDarkUrl : logoLightUrl;
  return preferred ?? fallback ?? null;
}

/**
 * Tenant-aware brand mark. Renders the tenant logo (light/dark variant by the
 * current theme) when a logo url is set, otherwise a styled text wordmark from
 * `brandName` in the tenant wordmark font, plus an optional tagline. With no
 * brand supplied it renders the neutral platform default wordmark.
 *
 * Replaces the hardcoded GreenteamBrand. Callers supply brand data as props;
 * authed surfaces feed it from the resolved brand, pre-auth surfaces pass
 * nothing and get the platform default.
 */
export function TenantBrand({
  brandName,
  tagline,
  logoLightUrl,
  logoDarkUrl,
  theme = 'dark',
  size = 'sm',
  showTagline = false,
  truncateWordmark = false,
  className,
}: TenantBrandProps) {
  const name = brandName?.trim() || PLATFORM_BRAND_NAME;
  // A logo url that 404s must degrade to the text wordmark, not the browser's
  // broken-image glyph (a rolled-back deploy or stale checkout can be missing
  // an app-served asset the branding row still points at). Failed urls are
  // dropped from variant selection, so one broken variant falls back to the
  // other, and losing both falls back to the wordmark.
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const markFailed = useCallback((url: string) => {
    setFailedUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);
  // React attaches onError at hydration, so a logo that 404s during a
  // server-rendered first paint can error BEFORE the handler exists and the
  // event is lost (Codex #1806). A ref callback runs at commit: if the browser
  // has already finished this image and decoded nothing, treat it as failed.
  // Checks the raw prop url (not img.src, which the browser absolutizes) so
  // the key matches the failedUrls lookups.
  const detectPreHydrationFailure = useCallback(
    (img: HTMLImageElement | null, url: string) => {
      if (img && img.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
        markFailed(url);
      }
    },
    [markFailed],
  );

  const liveLight = logoLightUrl && !failedUrls.has(logoLightUrl) ? logoLightUrl : null;
  const liveDark = logoDarkUrl && !failedUrls.has(logoDarkUrl) ? logoDarkUrl : null;
  const logoUrl = logoForTheme(theme, liveLight, liveDark);
  const withTagline = showTagline && Boolean(tagline?.trim());

  // max-w-full keeps a wide horizontal wordmark logo bound to its container
  // (the max-w-md auth card / the sidebar) instead of overflowing and getting
  // clipped by the card's overflow-hidden. object-contain preserves aspect
  // ratio within the fixed height.
  const logoClassName = cn('w-auto max-w-full object-contain', logoSizes[size]);

  return (
    <span
      className={cn(
        'inline-flex flex-col items-center gap-1 text-foreground',
        // A flex item defaults to min-width:auto, which refuses to shrink below
        // its content. The wordmark's max-w-full below therefore only bites once
        // this box is allowed to shrink in the first place.
        truncateWordmark && 'min-w-0',
        className,
      )}
    >
      {liveLight && liveDark && liveLight !== liveDark ? (
        // Both variants set: render both and let the `dark` CSS variant pick.
        // The `theme` prop is server-seeded and does NOT update when the theme
        // toggle flips the <html> class client-side without a reload, so a
        // prop-driven single <img> would keep the wrong variant on screen
        // (e.g. a black-on-transparent light logo on the dark background).
        // Keying on the live <html> class tracks the toggle instantly.
        // Plain <img>: tenant logos live on arbitrary CDN hosts, so we skip
        // next/image (which needs per-host allowlisting). The alt carries the
        // brand name so the mark stays announced; the hidden variant is
        // display:none and not announced.
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={liveLight}
            alt={name}
            ref={(el) => detectPreHydrationFailure(el, liveLight)}
            onError={() => markFailed(liveLight)}
            className={cn(logoClassName, 'dark:hidden')}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={liveDark}
            alt={name}
            ref={(el) => detectPreHydrationFailure(el, liveDark)}
            onError={() => markFailed(liveDark)}
            className={cn(logoClassName, 'hidden dark:block')}
          />
        </>
      ) : logoUrl ? (
        // Single variant (or both identical): theme-tracking is moot, keep the
        // prop-driven pick with its cross-variant fallback.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={logoUrl}
          alt={name}
          ref={(el) => detectPreHydrationFailure(el, logoUrl)}
          onError={() => markFailed(logoUrl)}
          className={logoClassName}
        />
      ) : (
        <span
          // font-wordmark maps to var(--brand-font-wordmark, var(--font-sans))
          // via design-tokens.css, so the platform default stays on the
          // default sans and is byte-identical to today.
          className={cn(
            'font-wordmark font-medium tracking-tight',
            wordmarkSizes[size],
            // One line, clipped with an ellipsis. Without this a multiword name
            // wraps (blowing a fixed-height bar) and an unbroken one paints over
            // whatever sits next to it.
            truncateWordmark && 'max-w-full truncate',
          )}
        >
          {name}
        </span>
      )}
      {withTagline ? (
        <span className={cn('text-muted-foreground', taglineSizes[size])}>{tagline}</span>
      ) : null}
    </span>
  );
}
